// Suggestions.js
import React, { useState, useEffect } from 'react';
import useAuth from '../hooks/useAuth';
import { 
  getAllSuggestions, 
  createSuggestion, 
  voteSuggestion, 
  deleteSuggestion 
} from '../api/suggestions';
import Button from '../components/ui/Button';
import './Suggestions.css';

const Suggestions = () => {
  const { user, userData } = useAuth();
  const [suggestions, setSuggestions] = useState([]);
  const [suggestionText, setSuggestionText] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  useEffect(() => {
    fetchSuggestions();
  }, []);

  const fetchSuggestions = async () => {
    try {
      setLoading(true);
      const suggestionsData = await getAllSuggestions();
      setSuggestions(suggestionsData);
      setError(null);
    } catch (err) {
      console.error('Error fetching suggestions:', err);
      setError('Failed to load suggestions. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!suggestionText.trim()) {
      setError('Please enter a suggestion');
      return;
    }

    if (!user || !userData) {
      setError('You must be logged in to submit a suggestion');
      return;
    }

    try {
      setSubmitting(true);
      setError(null);
      
      const userName = `${userData.firstName || ''} ${userData.lastName || ''}`.trim() || 'Anonymous';
      await createSuggestion(user.uid, userName, suggestionText);
      
      setSuggestionText('');
      setSuccess('Suggestion submitted successfully!');
      setTimeout(() => setSuccess(null), 3000);
      
      // Refresh suggestions list
      await fetchSuggestions();
    } catch (err) {
      console.error('Error submitting suggestion:', err);
      setError('Failed to submit suggestion. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleVote = async (suggestionId, voteType) => {
    if (!user) {
      setError('You must be logged in to vote');
      setTimeout(() => setError(null), 3000);
      return;
    }

    try {
      await voteSuggestion(suggestionId, user.uid, voteType);
      
      // Refresh suggestions list to show updated votes
      await fetchSuggestions();
    } catch (err) {
      console.error('Error voting:', err);
      setError('Failed to register vote. Please try again.');
      setTimeout(() => setError(null), 3000);
    }
  };

  const handleDelete = async (suggestionId) => {
    if (!window.confirm('Are you sure you want to delete this suggestion?')) {
      return;
    }

    try {
      await deleteSuggestion(suggestionId);
      setSuccess('Suggestion deleted successfully!');
      setTimeout(() => setSuccess(null), 3000);
      
      // Refresh suggestions list
      await fetchSuggestions();
    } catch (err) {
      console.error('Error deleting suggestion:', err);
      setError('Failed to delete suggestion. Please try again.');
      setTimeout(() => setError(null), 3000);
    }
  };

  const getUserVoteStatus = (suggestion) => {
    if (!user) return null;
    
    const upvotes = suggestion.upvotes || [];
    const downvotes = suggestion.downvotes || [];
    
    if (upvotes.includes(user.uid)) return 'up';
    if (downvotes.includes(user.uid)) return 'down';
    return null;
  };

  const canDelete = (suggestion) => {
    if (!user || !userData) return false;
    // User can delete their own suggestions or admin can delete any
    return suggestion.userId === user.uid || userData.role === 'admin';
  };

  if (loading) {
    return (
      <div className="page suggestions-page">
        <div className="loading-spinner">Loading suggestions...</div>
      </div>
    );
  }

  return (
    <div className="page suggestions-page">
      <div className="suggestions-header">
        <h1>Suggestions</h1>
        <p>Share your ideas to improve our community</p>
      </div>

      {/* Alert Messages */}
      {error && <div className="alert alert-error">{error}</div>}
      {success && <div className="alert alert-success">{success}</div>}

      {/* Submit Suggestion Form */}
      {user && (
        <div className="suggestion-form-container">
          <h2>Submit a New Suggestion</h2>
          <form onSubmit={handleSubmit} className="suggestion-form">
            <textarea
              placeholder="Enter your suggestion here..."
              value={suggestionText}
              onChange={(e) => setSuggestionText(e.target.value)}
              rows="5"
              maxLength={500}
              required
            />
            <div className="form-footer">
              <span className="char-count">{suggestionText.length}/500</span>
              <Button type="submit" disabled={submitting || !suggestionText.trim()}>
                {submitting ? 'Submitting...' : 'Submit Suggestion'}
              </Button>
            </div>
          </form>
        </div>
      )}

      {!user && (
        <div className="login-prompt">
          <p>Please log in to submit suggestions</p>
        </div>
      )}

      {/* Suggestions List */}
      <div className="suggestions-list-container">
        <h2>All Suggestions ({suggestions.length})</h2>
        
        {suggestions.length === 0 ? (
          <div className="no-suggestions">
            <p>No suggestions yet. Be the first to submit one!</p>
          </div>
        ) : (
          <div className="suggestions-list">
            {suggestions.map((suggestion) => {
              const userVote = getUserVoteStatus(suggestion);
              const votesCount = suggestion.votesCount || 0;
              
              return (
                <div key={suggestion.id} className="suggestion-card">
                  <div className="suggestion-vote">
                    <button
                      className={`vote-btn vote-up ${userVote === 'up' ? 'active' : ''}`}
                      onClick={() => handleVote(suggestion.id, 'up')}
                      disabled={!user}
                      title={user ? 'Upvote' : 'Login to vote'}
                    >
                      ▲
                    </button>
                    <span className={`vote-count ${votesCount > 0 ? 'positive' : votesCount < 0 ? 'negative' : ''}`}>
                      {votesCount}
                    </span>
                    <button
                      className={`vote-btn vote-down ${userVote === 'down' ? 'active' : ''}`}
                      onClick={() => handleVote(suggestion.id, 'down')}
                      disabled={!user}
                      title={user ? 'Downvote' : 'Login to vote'}
                    >
                      ▼
                    </button>
                  </div>
                  
                  <div className="suggestion-content">
                    <p className="suggestion-text">{suggestion.text}</p>
                    <div className="suggestion-meta">
                      <span className="suggestion-author">
                        By {suggestion.userName || 'Anonymous'}
                      </span>
                      <span className="suggestion-date">
                        {suggestion.createdAt?.toDate ? 
                          suggestion.createdAt.toDate().toLocaleDateString() : 
                          'Recently'}
                      </span>
                      {canDelete(suggestion) && (
                        <button
                          className="delete-btn"
                          onClick={() => handleDelete(suggestion.id)}
                          title="Delete suggestion"
                        >
                          🗑️ Delete
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default Suggestions;
